import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getNavItems } from '.';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';
import { roles } from 'constants/constant';
import { getUserDetails } from 'auth/auth';
import fundmoreLogo from '/fundmoreai.png';
import { performLogout } from 'utils/logout';
import Modal from 'components/modal';
import Button from 'components/button';
import NotificationsModal from 'components/notifications-modal';
import ProfileSectionModal from 'components/profile-section-modal';
import { isBulkUploadInProgress } from 'utils/bulkUploadTracker';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '../LanguageToggle/LanguageToggle';
import {
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
} from 'redux_store/services/notifications';
import { formatTimeDifferenceUTC } from 'constants/updatedAt';
import styles from '../../styles/components/nav-bar.module.css';
import {
  LayoutGrid,
  FileText,
  BarChart3,
  Users,
  ClipboardList,
  User,
  FolderOpen,
  LogOut,
  Bell,
  X,
  Activity,
} from 'lucide-react';

/**
 * Renders a navigation bar component.
 *
 * @returns {JSX.Element} The rendered navigation bar component.
 */
const NavBar = () => {
  const { t, i18n } = useTranslation(['navigation', 'common']);
  const { userRole } = useSelector(userRoleData);
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(() => getUserDetails());
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [highlightNotificationId, setHighlightNotificationId] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Re-read user details from cache when role changes (signals auth completed)
  useEffect(() => {
    setUserDetails(getUserDetails());
  }, [userRole]);

  const { firstName, lastName, userEmail } = userDetails;
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const showUnread = false;

  const navItems = useMemo(() => getNavItems(t, userRole), [t, i18n.language, userRole]);

  const filteredNavItems = useMemo(() => {
    return navItems?.filter((item) => {
      // Explicitly hide Reports for loan processors
      if (userRole === roles.loanProcessor && item.title === 'Reports') {
        return false;
      }
      return item?.role?.includes(userRole);
    });
  }, [navItems, userRole]);

  const iconMap = {
    Dashboard: LayoutGrid,
    Profile: User,
    Pipeline: FileText,
    Documents: FolderOpen,
    Tools: BarChart3,
    Admin: Users,
    'Activity Log': Activity,
    'User Management': Users,
    Reports: ClipboardList,
  };

  const {
    currentData: notificationData,
    isFetching: notificationFetching,
  } = useGetNotificationsQuery(
    { showUnread },
    { pollingInterval: 60000 }
  );
  const [updateNotification] = useUpdateNotificationsMutation();
  const notificationList = notificationData?.data?.data ?? [];
  const unreadCount = notificationList.filter(
    (item) => item.isRead === '0'
  ).length;

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((name) => name?.[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const displayName =
    `${firstName ?? ''} ${lastName ?? ''}`.trim() || t('common:unknownUser');
  const displayEmail = userEmail || t('common:noEmailOnFile');
  const avatarInitials =
    initials ||
    (userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : '?');
  
  const handleLogout = () => {
    if (isBulkUploadInProgress()) {
      setShowLogoutConfirm(true);
      return;
    }
    performLogout();
  };

  // Bell-panel selection opens the All-notifications dialog and spotlights
  // the picked row. Selecting a notification deliberately does NOT navigate
  // anywhere — it supersedes the PROD-739 deep-link into the application,
  // which the dialog rows no longer do either.
  const handleNotificationSelect = async (notification) => {
    const { userNotificationId, isRead } = notification;
    if (isRead === '0') {
      await updateNotification({
        data: { notification_ids: [userNotificationId] },
      });
    }
    setShowNotifications(false);
    setHighlightNotificationId(userNotificationId);
    setShowAllNotifications(true);
  };

  const renderIcon = (item, isActive) => {
    if (React.isValidElement(item?.icon)) {
      return React.cloneElement(item.icon, {
        className: `${styles.navIcon} ${styles.navIconImage} ${
          isActive ? styles.navIconActive : styles.navIconInactive
        }`,
      });
    }

    const IconComponent = iconMap[item.title];
    if (IconComponent) {
      return (
        <IconComponent
          className={`${styles.navIcon} ${
            isActive ? styles.navIconActive : styles.navIconInactive
          }`}
        />
      );
    }

    return null;
  };

  const isPathActive = (path, subPaths = []) => {
    const currentPath = location.pathname;
    if (currentPath.endsWith(path)) {
      return true;
    }
    return subPaths?.some((subPath) => currentPath.startsWith(subPath));
  };

  return (
    <aside
      className={`${styles.sidebar} group ${showNotifications ? styles.sidebarNotificationOpen : ''}`}
    >
      <div>
        <div className={styles.logoRow}>
          <div className={styles.logoGroup}>
            <div className={styles.logoIcon}>
              <img
                src={fundmoreLogo}
                alt="fundmore.ai"
                className={styles.logoImage}
              />
            </div>
            <div className={styles.logoText}>
              <span>
                <span className={styles.logoLight}>fund</span>
                <span className={styles.logoBold}>more</span>
                <span className={styles.logoAccent}>.ai</span>
              </span>
            </div>
          </div>
        </div>

        <nav className={styles.navList}>
          {filteredNavItems?.map((item, index) => {
            const isActive = isPathActive(item.path, item.subPaths);
            const label = item.label ?? item.title;
            return (
              <Link
                key={`${item.title}-${index}`}
                to={item.path}
                className={`${styles.actionButton} ${
                  isActive ? styles.actionButtonActive : styles.actionButtonInactive
                }`}
              >
                <div className={styles.navIconWrapper}>
                  {renderIcon(item, isActive)}
                </div>
                <span
                  className={`${styles.navLabel} ${
                    isActive ? styles.navLabelActive : ''
                  }`}
                >
                  {label}
                </span>
                <div className={styles.tooltip}>
                  {label}
                  <div className={styles.tooltipArrow} />
                </div>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className={styles.footer}>
        <div className={styles.notificationWrapper}>
          {showNotifications && (
            <div
              className={`${styles.notificationPopup} ${styles.notificationPopupInline}`}
            >
              <div className={styles.notificationHeader}>
                <div className={styles.notificationHeaderTitle}>
                  <div className={styles.notificationHeaderIconWrapper}>
                    <Bell
                      className={`${styles.navIcon} ${styles.notificationHeaderIcon}`}
                    />
                    {unreadCount > 0 && (
                      <span className={styles.notificationBadgeDot} />
                    )}
                  </div>
                  <span>{t('navigation:notifications')}</span>
                </div>
                <div className={styles.notificationHeaderActions}>
                  <button
                    type="button"
                    className={styles.notificationCloseButton}
                    onClick={() => setShowNotifications(false)}
                    aria-label={t('common:buttons.close', {
                      defaultValue: 'Close notifications',
                      keySeparator: false,
                    })}
                  >
                    <X className={styles.notificationCloseIcon} />
                  </button>
                </div>
              </div>
              <div className={styles.notificationList}>
                {notificationFetching && (
                  <div className={styles.notificationEmpty}>
                    {t('common:loadingNotifications')}
                  </div>
                )}
                {!notificationFetching && notificationList.length === 0 && (
                  <div className={styles.notificationEmpty}>
                    {t('common:noNewNotifications')}
                  </div>
                )}
                {!notificationFetching &&
                  notificationList.map((notification) => {
                    const isUnread = notification.isRead === '0';
                    const subject = notification.subject || t('common:notification');
                    const message = notification.message || '';
                    const timeLabel = notification?.createdAt
                      ? formatTimeDifferenceUTC(notification.createdAt)
                      : '';
                    return (
                      <div
                        key={notification.userNotificationId}
                        className={`${styles.notificationItem} ${
                          isUnread ? styles.notificationItemUnread : ''
                        }`}
                        onClick={() => handleNotificationSelect(notification)}
                      >
                        <h4
                          className={
                            isUnread
                              ? styles.notificationTitle
                              : styles.notificationTitleRead
                          }
                        >
                          {subject}
                        </h4>
                        <p className={styles.notificationMessage}>{message}</p>
                        <span className={styles.notificationTime}>
                          {timeLabel}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className={styles.notificationFooter}>
                <button
                  className={styles.notificationFooterButton}
                  onClick={() => {
                    setShowNotifications(false);
                    setHighlightNotificationId(null);
                    setShowAllNotifications(true);
                  }}
                >
                  {t('common:viewAllNotifications')}
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => setShowNotifications((open) => !open)}
            className={`${styles.actionButton} ${styles.actionButtonInactive}`}
            aria-label={t('navigation:notifications')}
            aria-expanded={showNotifications}
          >
            <div className={styles.navIconWrapper}>
              <Bell className={`${styles.navIcon} ${styles.navIconInactive}`} />
              {unreadCount > 0 && (
                <span className={styles.notificationBadgeDot} />
              )}
            </div>
            <span className={styles.navLabel}>
              {t('navigation:notifications')}
            </span>
            <div className={styles.tooltip}>
              {t('navigation:notifications')}
              <div className={styles.tooltipArrow} />
            </div>
          </button>
        </div>

        <div className={styles.languageToggle}>
          <LanguageToggle variant="sidebar" forceExpanded={showNotifications} />
          <span className={styles.languageLabel}>
            {t('navigation:language')}
          </span>
        </div>

        {/* PROD-796: clicking the profile row opens the profile section
            modal, which is where an MS edits their MS Code (PROD-256). */}
        <button
          type="button"
          className={styles.profileRow}
          onClick={() => setShowProfileModal(true)}
          aria-label={t('navigation:profileModal.openProfile')}
        >
          <div className={styles.profileAvatar}>
            {/* PROD-442: fallback derives from the email local-part when
                firstName/lastName aren't available (used to render a
                nonsensical hardcoded "SG" — looked like a specific
                person's initials to reporters). */}
            {avatarInitials}
          </div>
          <div className={styles.profileText}>
            <p className={styles.profileName}>{displayName}</p>
            <p className={styles.profileEmail}>{displayEmail}</p>
          </div>
        </button>

        <button
          className={styles.signOutButton}
          onClick={handleLogout}
          aria-label={t('common:buttons.signOut')}
        >
          <LogOut className={styles.navIcon} />
          <span className={styles.navLabel}>
            {t('common:buttons.signOut')}
          </span>
          <div className={styles.tooltip}>
            {t('common:buttons.signOut')}
            <div className={styles.tooltipArrow} />
          </div>
        </button>
      </div>

      <ProfileSectionModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        displayName={displayName}
        displayEmail={displayEmail}
        initials={avatarInitials}
        isBorrower={userRole === roles.consumer}
      />

      {showAllNotifications && (
        <NotificationsModal
          show={showAllNotifications}
          onClose={() => {
            setShowAllNotifications(false);
            setHighlightNotificationId(null);
          }}
          notifications={notificationList}
          highlightId={highlightNotificationId}
        />
      )}

      {showLogoutConfirm &&
        createPortal(
          <Modal
            show={showLogoutConfirm}
            width="max-w-[420px]"
            onClose={() => setShowLogoutConfirm(false)}
          >
          <div className="p-5">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('documents:bulkUpload.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {t(
                'documents:bulkUpload.signOutWarning',
                {
                  defaultValue:
                    'A document is still processing. Do you want to sign out anyway?',
                  keySeparator: false,
                }
              )}
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="tertiary"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('common:buttons.no', { defaultValue: 'No', keySeparator: false })}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowLogoutConfirm(false);
                  performLogout();
                }}
              >
                {t('common:buttons.yes', { defaultValue: 'Yes', keySeparator: false })}
              </Button>
            </div>
          </div>
          </Modal>,
          document.body
        )}
    </aside>
  );
};

export default NavBar;
